/**
 * Filter bar for the asset list.
 *
 * `pilotReady` is the important control. It answers the matchmaker's question
 * (F11) — which sites could host a pilot today — in one click, rather than
 * making a user tick five separate conditions. A filter nobody can assemble is
 * a filter nobody runs.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.4, PRD §5.6
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ASSET_OWNERSHIPS,
  ASSET_STATUSES,
  ASSET_TYPES,
} from '../../../components/domain/asset-taxonomy';
import { Button } from '../../../components/ui/Button';
import { Dialog } from '../../../components/ui/Dialog';
import { Icon } from '../../../components/ui/Icon';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { filterHandler } from '../../../lib/filter-handler';

export interface AssetFiltersProps {
  filters: Record<string, string>;
  onFilterChange: (key: string, value: string) => void;
  onClear: () => void;
}

/** The controls, shared by the inline bar and the sheet. */
function FilterControls({
  filters,
  onFilterChange,
}: Omit<AssetFiltersProps, 'onClear'>): React.JSX.Element {
  const { t } = useTranslation();

  /** Flips a boolean filter, clearing it rather than storing `"false"`. */
  const toggle = (key: string): void => {
    onFilterChange(key, filters[key] === 'true' ? '' : 'true');
  };

  return (
    <>
      <label className="flex-1">
        <span className="sr-only">{t('asset.filters.search')}</span>
        <Input
          type="search"
          value={filters['q'] ?? ''}
          onChange={(event) => {
            onFilterChange('q', event.target.value);
          }}
          placeholder={t('asset.filters.searchPlaceholder')}
        />
      </label>

      <Select
        value={filters['type']}
        onValueChange={filterHandler('type', onFilterChange)}
        options={ASSET_TYPES.map((type) => ({
          value: type,
          label: t(`asset.type.${type}`),
        }))}
        placeholder={t('asset.filters.anyType')}
        ariaLabel={t('asset.filters.type')}
        className="sm:w-52"
      />

      <Select
        value={filters['status']}
        onValueChange={filterHandler('status', onFilterChange)}
        options={ASSET_STATUSES.map((status) => ({
          value: status,
          label: t(`asset.status.${status}`),
        }))}
        placeholder={t('asset.filters.anyStatus')}
        ariaLabel={t('asset.filters.status')}
        className="sm:w-44"
      />

      <Select
        value={filters['ownership']}
        onValueChange={filterHandler('ownership', onFilterChange)}
        options={ASSET_OWNERSHIPS.map((ownership) => ({
          value: ownership,
          label: t(`asset.ownership.${ownership}`),
        }))}
        placeholder={t('asset.filters.anyOwnership')}
        ariaLabel={t('asset.filters.ownership')}
        className="sm:w-48"
      />

      {/* The matchmaker's question in one click (F11). */}
      <Button
        variant={filters['pilotReady'] === 'true' ? 'primary' : 'secondary'}
        size="sm"
        aria-pressed={filters['pilotReady'] === 'true'}
        onClick={() => {
          toggle('pilotReady');
        }}
      >
        {t('asset.filters.pilotReady')}
      </Button>

      <Button
        variant={filters['blocked'] === 'true' ? 'primary' : 'secondary'}
        size="sm"
        aria-pressed={filters['blocked'] === 'true'}
        onClick={() => {
          toggle('blocked');
        }}
      >
        {t('asset.filters.blocked')}
      </Button>
    </>
  );
}

export function AssetFilters({
  filters,
  onFilterChange,
  onClear,
}: AssetFiltersProps): React.JSX.Element {
  const { t } = useTranslation();
  const [sheetOpen, setSheetOpen] = useState(false);

  const appliedCount = Object.keys(filters).length;

  return (
    <>
      {/* Inline bar, 1024px and up. Six controls need more room than the other
          lists' three, so the inline form starts at `lg` rather than `md`. */}
      <div className="gap-s2 hidden flex-wrap items-center lg:flex">
        <FilterControls filters={filters} onFilterChange={onFilterChange} />
        {appliedCount > 0 ? (
          <Button variant="ghost" size="sm" onClick={onClear}>
            {t('asset.filters.clear')}
          </Button>
        ) : null}
      </div>

      {/* Below 1024px: one button opening a sheet, with the applied count on it
          so active filters are visible without opening it. */}
      <div className="lg:hidden">
        <Button
          variant="secondary"
          onClick={() => {
            setSheetOpen(true);
          }}
          className="w-full"
        >
          <Icon name="search" />
          {appliedCount === 0
            ? t('actions.filter')
            : t('asset.filters.applied', { count: appliedCount })}
        </Button>

        <Dialog
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          title={t('actions.filter')}
          footer={
            <>
              <Button variant="ghost" onClick={onClear}>
                {t('asset.filters.clear')}
              </Button>
              <Button
                onClick={() => {
                  setSheetOpen(false);
                }}
              >
                {t('actions.filter')}
              </Button>
            </>
          }
        >
          <div className="gap-s3 flex flex-col">
            <FilterControls filters={filters} onFilterChange={onFilterChange} />
          </div>
        </Dialog>
      </div>
    </>
  );
}
